import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Select } from 'antd';

import {
  MONTH_NAMES,
  UtilityBill,
} from '@totem/components/portfolio/buildings/details/utilities/types';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { BUILDINGS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

const styles = {
  row: {
    display: 'flex',
    gap: '4%',
  },
  halfWidth: {
    paddingBottom: '0',
    marginBottom: '1rem',
    width: '48%',
  },
  thirdWidth: {
    paddingBottom: '0',
    marginBottom: '1rem',
    width: '30%',
  },
  formItem: {
    paddingBottom: '0',
    marginBottom: '1rem',
    width: '100%',
  },
};

type FormState = {
  year: number;
  month: number;
  billingDays: number | null;
  kwh: number;
  energyCost: number;
  peakKw: number;
  demandCost: number;
  totalCost: number;
  notes: string;
};

const currentYear = new Date().getFullYear();

const emptyBillForm = (): FormState => ({
  year: currentYear,
  month: new Date().getMonth() + 1,
  billingDays: null,
  kwh: 0,
  energyCost: 0,
  peakKw: 0,
  demandCost: 0,
  totalCost: 0,
  notes: '',
});

type Props = {
  open: boolean;
  buildingId: string;
  accountId: string;
  bill?: UtilityBill;
  onClose: () => void;
  onSaved: () => void;
};

const UtilityBillModal = ({
  open,
  buildingId,
  accountId,
  bill,
  onClose,
  onSaved,
}: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [isSending, setIsSending] = useState(false);
  const [form, setForm] = useState<FormState>(emptyBillForm());

  const isEditing = !!bill;

  useEffect(() => {
    if (open) {
      setForm(
        bill
          ? {
              year: bill.year,
              month: bill.month,
              billingDays: bill.billingDays ?? null,
              kwh: bill.kwh,
              energyCost: bill.energyCost,
              peakKw: bill.peakKw,
              demandCost: bill.demandCost,
              totalCost: bill.totalCost,
              notes: bill.notes ?? '',
            }
          : emptyBillForm(),
      );
    }
  }, [open, bill]);

  const handleSubmit = useCallback(() => {
    if (isSending) {
      return;
    }

    setIsSending(true);

    const url = isEditing
      ? `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/utility-bills/${bill.id}`
      : `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/utility-accounts/${accountId}/bills`;

    const method = isEditing ? 'PATCH' : 'POST';

    fetch(url, {
      method,
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify(form),
    })
      .then((res) => {
        CheckResponseShowError(res);

        onSaved();

        onClose();
      })
      .finally(() => setIsSending(false));
  }, [
    form,
    isSending,
    isEditing,
    buildingId,
    accountId,
    bill,
    getApiToken,
    onSaved,
    onClose,
  ]);

  const yearOptions = Array.from({ length: 10 }, (_, idx) => currentYear - idx);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={520}
      title={
        <ModalTitle>
          {isEditing ? 'Edit Utility Bill' : 'Add Utility Bill'}
        </ModalTitle>
      }
      footer={
        <ModalFooter>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={isSending} onClick={handleSubmit}>
            {isEditing ? 'Save' : 'Add'}
          </Button>
        </ModalFooter>
      }
    >
      <Form layout="vertical">
        <div style={styles.row}>
          <Form.Item label="Year" style={styles.halfWidth} required>
            <Select
              value={form.year}
              onChange={(val) => setForm({ ...form, year: val })}
              style={{ width: '100%' }}
            >
              {yearOptions.map((yr) => (
                <Select.Option key={yr} value={yr}>
                  {yr}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Month" style={styles.halfWidth} required>
            <Select
              value={form.month}
              onChange={(val) => setForm({ ...form, month: val })}
              style={{ width: '100%' }}
            >
              {MONTH_NAMES.map((name, idx) => (
                <Select.Option key={idx + 1} value={idx + 1}>
                  {name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <div style={styles.row}>
          <Form.Item label="kWh Usage" style={styles.halfWidth} required>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              value={form.kwh}
              onChange={(val) => setForm({ ...form, kwh: val ?? 0 })}
            />
          </Form.Item>
          <Form.Item label="Billing Days" style={styles.halfWidth}>
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              max={31}
              value={form.billingDays}
              onChange={(val) => setForm({ ...form, billingDays: val })}
              placeholder="Optional"
            />
          </Form.Item>
        </div>

        <div style={styles.row}>
          <Form.Item label="Energy Cost ($)" style={styles.halfWidth} required>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              value={form.energyCost}
              onChange={(val) => setForm({ ...form, energyCost: val ?? 0 })}
            />
          </Form.Item>
          <Form.Item label="Peak kW" style={styles.halfWidth}>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              value={form.peakKw}
              onChange={(val) => setForm({ ...form, peakKw: val ?? 0 })}
            />
          </Form.Item>
        </div>

        <div style={styles.row}>
          <Form.Item label="Demand Cost ($)" style={styles.halfWidth}>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              value={form.demandCost}
              onChange={(val) => setForm({ ...form, demandCost: val ?? 0 })}
            />
          </Form.Item>
          <Form.Item label="Total Cost ($)" style={styles.halfWidth} required>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              value={form.totalCost}
              onChange={(val) => setForm({ ...form, totalCost: val ?? 0 })}
            />
          </Form.Item>
        </div>

        <Form.Item label="Notes" style={styles.formItem}>
          <Input.TextArea
            rows={2}
            value={form.notes}
            onChange={(evt) => setForm({ ...form, notes: evt.target.value })}
            placeholder="Optional"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UtilityBillModal;
